import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { adminApiService } from '../../services/adminApi';
import { TableCard, TableHeader, TableViewport } from '../table/TableKit';
import {
  Badge,
  EmptyRow,
  ErrorNotice,
  LoadingSpinner,
  StatTile,
  formatNumber,
  instanceLabel,
  tdClass,
  thClass,
} from './shared';
import RelativeTime from '../RelativeTime';

function AdminOverview() {
  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'overview'],
    queryFn: adminApiService.getOverview,
    retry: false,
  });

  if (isLoading) return <LoadingSpinner />;
  if (error || !data) return <ErrorNotice message="Failed to load overview." />;

  const jobStatuses = ['pending', 'running', 'completed', 'failed', 'cancelled'];

  return (
    <div>
      <h2 className="text-xl font-bold text-heading mb-4">Overview</h2>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <StatTile
          label="Instances"
          value={formatNumber(data.instances.total)}
          sub={`${data.instances.active} active · ${data.instances.persistent} persistent · +${data.instances.new_7d} this week`}
        />
        <StatTile
          label="Jobs"
          value={formatNumber(data.jobs.total)}
          sub={`${data.jobs.last_24h} last 24h · ${data.jobs.last_7d} last 7d`}
        />
        <StatTile label="Queue Depth" value={formatNumber(data.queue_depth)} />
        <StatTile
          label="API Keys"
          value={formatNumber(data.api_keys.total)}
          sub={`${data.api_keys.active} active`}
        />
        <StatTile
          label="API Conversions"
          value={formatNumber(data.api_usage.conversions_total)}
          sub={`${data.api_usage.conversions_7d} last 7d · ${data.api_usage.errors_7d} errors 7d`}
        />
        <StatTile
          label="API Converted Tokens"
          value={formatNumber(data.api_usage.input_tokens_converted_total)}
          sub={`${formatNumber(data.api_usage.input_tokens_converted_7d)} last 7d`}
        />
        <StatTile
          label="CLI Operations"
          value={formatNumber(data.cli_usage.operations_total)}
          sub={`${data.cli_usage.operations_7d} last 7d · ${formatNumber(data.cli_usage.unique_total)} unique / ${formatNumber(data.cli_usage.duplicate_total)} duplicate`}
        />
        <StatTile
          label="Contact Requests"
          value={formatNumber(data.contact_requests.total)}
          sub={`${data.contact_requests.last_7d} last 7d`}
        />
      </div>

      <div className="grid md:grid-cols-2 gap-6 mb-6">
        <div className="bg-widget-bg rounded-lg border border-gray-700 p-4">
          <h3 className="text-sm font-medium text-secondary uppercase tracking-wider mb-3">Jobs by Status</h3>
          <div className="space-y-2">
            {jobStatuses.map((status) => (
              <div key={status} className="flex items-center justify-between text-sm">
                <Badge value={status} />
                <span className="text-body-color font-mono">{formatNumber(data.jobs.by_status[status] ?? 0)}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="bg-widget-bg rounded-lg border border-gray-700 p-4">
          <h3 className="text-sm font-medium text-secondary uppercase tracking-wider mb-3">Jobs by Operation</h3>
          <div className="space-y-2">
            {Object.entries(data.jobs.by_operation)
              .sort((a, b) => b[1] - a[1])
              .map(([operation, count]) => (
                <div key={operation} className="flex items-center justify-between text-sm">
                  <span className="text-body-color">{operation}</span>
                  <span className="text-body-color font-mono">{formatNumber(count)}</span>
                </div>
              ))}
            {Object.keys(data.jobs.by_operation).length === 0 && (
              <div className="text-secondary text-sm">No jobs yet.</div>
            )}
          </div>
        </div>
      </div>

      <TableCard>
        <TableHeader title="Recent Failed Jobs">
          <Link to="/admin/jobs" className="ml-auto text-sm text-primary hover:text-primary-hover">
            View all jobs
          </Link>
        </TableHeader>
        <TableViewport>
        <table className="w-full">
          <thead>
            <tr className="border-b border-gray-700">
              <th className={thClass}>Job</th>
              <th className={thClass}>Instance</th>
              <th className={thClass}>Operation</th>
              <th className={thClass}>Error</th>
              <th className={thClass}>Failed At</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-700">
            {data.recent_failed_jobs.map((job) => (
              <tr key={job.id}>
                <td className={`${tdClass} font-mono`}>{job.id.slice(0, 8)}…</td>
                <td className={tdClass}>{instanceLabel(job.instance_name, job.instance_key)}</td>
                <td className={tdClass}>{job.operation}</td>
                <td className={`${tdClass} max-w-md truncate`} title={job.error_message ?? ''}>
                  {job.error_message || '—'}
                </td>
                <td className={tdClass}><RelativeTime value={job.completed_at} /></td>
              </tr>
            ))}
            {data.recent_failed_jobs.length === 0 && <EmptyRow colSpan={5} text="No failed jobs. 🎉" />}
          </tbody>
        </table>
        </TableViewport>
      </TableCard>
    </div>
  );
}

export default AdminOverview;
